import { useEffect, useState, useCallback, useRef } from 'react'
import { updateMatch, subscribeToMatch } from '@/services/firebase'

export function useTiebreakLogic(matchId: string, tiebreakTarget: number = 7) {
  const [tiebreakPoints, setTiebreakPoints] = useState({ team1: 0, team2: 0 })
  const [isTiebreakActive, setIsTiebreakActive] = useState(false)
  const lastLocalUpdateRef = useRef<{ team1: number; team2: number } | null>(null)
  const ignoreFirebaseUntil = useRef<number>(0) // ⏱️ For suppressing stale Firebase push

  // Subscribe to Firebase match data
  useEffect(() => {
    const unsubscribe = subscribeToMatch(matchId, (matchData) => {
      const now = Date.now()
      const firebasePoints = {
        team1: matchData?.tiebreakPoints?.team1 ?? 0,
        team2: matchData?.tiebreakPoints?.team2 ?? 0,
      }

      if (matchData?.isTiebreak !== undefined) {
        setIsTiebreakActive(matchData.isTiebreak)
      }

      // ⏳ Suppress update if inside cooldown window
      if (now < ignoreFirebaseUntil.current) {
        return
      }

      // Check if points have changed (for both increment and reverse)
      const hasChanged = 
        firebasePoints.team1 !== tiebreakPoints.team1 ||
        firebasePoints.team2 !== tiebreakPoints.team2

      if (
        !lastLocalUpdateRef.current ||
        hasChanged
      ) {
        setTiebreakPoints(firebasePoints)
      }
    })

    return () => unsubscribe()
  }, [matchId, tiebreakPoints])

  const incrementPoint = useCallback(
    async (team: 'team1' | 'team2') => {
      const updatedPoints = {
        ...tiebreakPoints,
        [team]: tiebreakPoints[team] + 1,
      }

      // 🧠 Store to prevent echo updates
      lastLocalUpdateRef.current = updatedPoints
      ignoreFirebaseUntil.current = Date.now() + 400

      setTiebreakPoints(updatedPoints)

      const p1 = updatedPoints.team1
      const p2 = updatedPoints.team2
      const hasWinner =
        (p1 >= tiebreakTarget || p2 >= tiebreakTarget) && Math.abs(p1 - p2) >= 2

      const updateData = {
        tiebreakPoints: updatedPoints,
        isTiebreak: !hasWinner,
      }

      try {
        await updateMatch(matchId, updateData)
        if (hasWinner) {
          setIsTiebreakActive(false)
        }
      } catch (error) {
        console.error('Error updating tiebreak:', error)
      }
    },
    [tiebreakPoints, tiebreakTarget, matchId]
  )

  const resetTiebreak = useCallback(() => {
    setTiebreakPoints({ team1: 0, team2: 0 })
    setIsTiebreakActive(false)
    lastLocalUpdateRef.current = null
    updateMatch(matchId, {
      tiebreakPoints: { team1: 0, team2: 0 },
      isTiebreak: false,
    })
  }, [matchId])

  return {
    tiebreakPoints,
    isTiebreakActive,
    setIsTiebreakActive,
    incrementPoint,
    resetTiebreak,
  }
} 